import { z } from 'zod';

import {
    ClientContextErrorCodes,
    validateOnboardingEdit,
    type ClientContextFailure,
    type OnboardingEditValues,
} from '../client-context/onboarding.js';
import { isBrandBrainEditableColumn } from './fields.js';
import { splitBrandBrainChanges, validateBrandBrainNexusChanges, type BrandBrainNexusChanges } from './nexus-fields.js';

// ─── Brand Brain update ──────────────────────────────────────────────
// Brand Brain edits go through the same onboarding update as the Identity
// Hub (TENANT_ONBOARDING_EDIT_REQUESTED); this narrows the client_context
// columns and adds the Nexus-owned fields, which only Brand Brain may send.

export const BrandBrainUpdateBodySchema = z.object({
    idempotencyKey: z.string().min(1),
    changes: z.record(z.unknown()),
});

export type BrandBrainUpdateValidation =
    | { ok: true; changes: OnboardingEditValues; nexusChanges: BrandBrainNexusChanges }
    | ClientContextFailure;

/** Brand Brain may change its placed client_context fields and its Nexus-owned fields; each is validated by its own registry. */
export function validateBrandBrainUpdate(changes: Record<string, unknown>): BrandBrainUpdateValidation {
    const split = splitBrandBrainChanges(changes);
    const locked = Object.keys(split.clientContext).find((field) => !isBrandBrainEditableColumn(field));
    if (locked !== undefined) {
        return {
            ok: false,
            code: ClientContextErrorCodes.FIELD_READ_ONLY,
            message: `${locked} cannot be edited from Brand Brain`,
            field: locked,
        };
    }
    if (Object.keys(split.nexus).length === 0) {
        const clientContext = validateOnboardingEdit(split.clientContext);
        return clientContext.ok ? { ok: true, changes: clientContext.changes, nexusChanges: {} } : clientContext;
    }
    const clientContext = Object.keys(split.clientContext).length === 0
        ? { ok: true as const, changes: {} }
        : validateOnboardingEdit(split.clientContext);
    if (!clientContext.ok) return clientContext;
    const nexus = validateBrandBrainNexusChanges(split.nexus);
    if (!nexus.ok) return nexus;
    return { ok: true, changes: clientContext.changes, nexusChanges: nexus.changes };
}
